/**
 * Supported interface languages. Mirrors `backend/app/models/language.py` —
 * the same list drives the UI language, the language the AI tutor answers in,
 * and the voice used to read that answer aloud.
 *
 * Keeping one list for all three means a student who switches the site to
 * Bengali also gets Bengali answers and a Bengali voice, instead of three
 * settings that can disagree.
 */

export type LanguageCode =
    | "en"
    | "hi"
    | "bn"
    | "mr"
    | "te"
    | "ta"
    | "gu"
    | "kn"
    | "ml"
    | "pa"
    | "or"
    | "as"
    | "ur";

export interface LanguageMeta {
    code: LanguageCode;
    /** English name — used in admin screens and logs. */
    label: string;
    /** The language's own name in its own script. This is what the picker shows:
     *  a student hunting for Bengali scans for "বাংলা", not for "Bengali". */
    nativeLabel: string;
    /** Full BCP-47 tag, needed to match a speech-synthesis voice. */
    bcp47: string;
    /** Right-to-left. Urdu only, but the flag has to exist or the layout
     *  silently breaks for those users. */
    rtl?: boolean;
}

export const LANGUAGES: LanguageMeta[] = [
    { code: "en", label: "English", nativeLabel: "English", bcp47: "en-IN" },
    { code: "hi", label: "Hindi", nativeLabel: "हिन्दी", bcp47: "hi-IN" },
    { code: "bn", label: "Bengali", nativeLabel: "বাংলা", bcp47: "bn-IN" },
    { code: "mr", label: "Marathi", nativeLabel: "मराठी", bcp47: "mr-IN" },
    { code: "te", label: "Telugu", nativeLabel: "తెలుగు", bcp47: "te-IN" },
    { code: "ta", label: "Tamil", nativeLabel: "தமிழ்", bcp47: "ta-IN" },
    { code: "gu", label: "Gujarati", nativeLabel: "ગુજરાતી", bcp47: "gu-IN" },
    { code: "kn", label: "Kannada", nativeLabel: "ಕನ್ನಡ", bcp47: "kn-IN" },
    { code: "ml", label: "Malayalam", nativeLabel: "മലയാളം", bcp47: "ml-IN" },
    { code: "pa", label: "Punjabi", nativeLabel: "ਪੰਜਾਬੀ", bcp47: "pa-IN" },
    { code: "or", label: "Odia", nativeLabel: "ଓଡ଼ିଆ", bcp47: "or-IN" },
    { code: "as", label: "Assamese", nativeLabel: "অসমীয়া", bcp47: "as-IN" },
    { code: "ur", label: "Urdu", nativeLabel: "اردو", bcp47: "ur-IN", rtl: true }
];

const BY_CODE = new Map(LANGUAGES.map((entry) => [entry.code, entry]));

export const DEFAULT_LANGUAGE: LanguageCode = "en";

/** Never throws — an unknown code resolves to English, which always exists. */
export const languageMeta = (code: string | null | undefined): LanguageMeta =>
    BY_CODE.get((code ?? "") as LanguageCode) ?? BY_CODE.get(DEFAULT_LANGUAGE)!;

export const isSupportedLanguage = (code: string | null | undefined): code is LanguageCode =>
    BY_CODE.has((code ?? "") as LanguageCode);
